import { supabase, tabela } from './supabase';
import { ORION_SCHEMA } from '../config/ambiente';

// ─────────────────────────────────────────────────────────────────────────────
// Notificações — a caixa de entrada do sininho (orion.notificacao).
//
// Cada linha é para UM operador, pelo código do MySuite, e quase sempre aponta
// para um ticket. Quem grava pode ser o próprio Orion (retorno do setor de
// Sistemas, apuração de pulo de fila) ou qualquer fluxo de fora — o n8n, um
// gatilho do banco. A tabela está em sql/criar-tabela_notificacao_*.sql.
// ─────────────────────────────────────────────────────────────────────────────

const TABELA = 'notificacao';

/** Quantas a caixa traz de uma vez — as mais recentes. */
const LIMITE = 200;

/** A mensagem do PostgREST quando a tabela ainda não foi criada. */
export const ehTabelaAusente = (msg) => /notificacao|does not exist|schema cache/i.test(String(msg || ''));

/**
 * As notificações do operador, da mais recente para a mais antiga.
 * Lança em caso de falha — o hook decide o que mostrar.
 */
export async function listarNotificacoes(codigo) {
  const { data, error } = await tabela(TABELA)
    .select('*')
    .eq('destinatario_codigo', Number(codigo))
    .order('criada_em', { ascending: false })
    .limit(LIMITE);
  if (error) throw new Error(error.message);
  return data || [];
}

/** Marca uma como lida. */
export async function marcarComoLida(id) {
  const { error } = await tabela(TABELA)
    .update({ lida_em: new Date().toISOString() })
    .eq('id', id)
    .is('lida_em', null);
  if (error) throw new Error(error.message);
}

/** Marca todas as não lidas do operador como lidas. */
export async function marcarTodasComoLidas(codigo) {
  const { error } = await tabela(TABELA)
    .update({ lida_em: new Date().toISOString() })
    .eq('destinatario_codigo', Number(codigo))
    .is('lida_em', null);
  if (error) throw new Error(error.message);
}

/**
 * Grava uma notificação. Nunca lança: notificar é acessório, e quem chama está
 * no meio de outra coisa (apurar um pulo, ler os retornos).
 *
 * Com `chave`, a mesma notificação não entra duas vezes para o mesmo operador
 * — a segunda gravação é ignorada pelo índice único.
 */
export async function criarNotificacao({
  destinatarioCodigo, destinatarioEmail = null, tipo = 'aviso',
  titulo, texto = '', numeroTicket = null, origem = 'Orion', chave = null,
}) {
  const codigo = Number(destinatarioCodigo);
  if (!codigo || !titulo) return false;
  const linha = {
    destinatario_codigo: codigo,
    destinatario_email: destinatarioEmail || null,
    tipo,
    titulo: String(titulo),
    texto: String(texto || ''),
    numero_ticket: numeroTicket ? Number(numeroTicket) : null,
    origem: origem || null,
    chave: chave || null,
  };
  try {
    const consulta = chave
      ? tabela(TABELA).upsert(linha, { onConflict: 'destinatario_codigo,chave', ignoreDuplicates: true })
      : tabela(TABELA).insert(linha);
    const { error } = await consulta;
    if (error) throw new Error(error.message);
    return true;
  } catch (err) {
    console.warn('[Orion] Notificação não gravada:', err.message);
    return false;
  }
}

/**
 * Avisa na hora quando chega notificação para `codigo`. Devolve a função que
 * desliga a assinatura. Sem realtime, o ciclo do hook continua trazendo.
 */
export function assinarNotificacoes(codigo, aoChegar) {
  const cod = Number(codigo);
  if (!cod) return () => {};
  let canal = null;
  try {
    canal = supabase
      .channel(`orion_notificacao_${cod}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: ORION_SCHEMA, table: TABELA,
        filter: `destinatario_codigo=eq.${cod}`,
      }, payload => aoChegar && aoChegar(payload.new))
      .subscribe();
  } catch (e) {
    console.warn('[Orion] Realtime de notificações:', e.message);
    return () => {};
  }
  return () => {
    try { supabase.removeChannel(canal); } catch (e) {}
  };
}
